import { RepositoryEntry } from 'store/repository'
import { DealHistoryUpdate, DealInterestPeriodUpdate, DealUpdate, EntityUpdateUnion, NegotiationUpdate, OrderUpdate } from 'api'
import { compareInsensitive, immUpdate, primitive } from 'utils'
import { orderBy } from 'lodash'

export function applyUpdatesForWindow<TUpdate extends EntityUpdateUnion>(
  entry: RepositoryEntry<TUpdate>,
  windowId: string | undefined
): TUpdate['entity'] {
  if (!windowId) {
    throw new Error('windowId has not been set')
  }
  return entry.fieldUpdates
    .filter(update => update.meta.sourceWindowId === windowId)
    .reduce((result, windowUpdate) => {
      return immUpdate(result, windowUpdate.payload.fieldName, windowUpdate.payload.fieldValue)
    }, entry.lastUpdate.entity)
}

export function getMetaValueForWindow<T extends primitive | null>(
  entry: RepositoryEntry<any>,
  metaName: string,
  windowId: string | undefined
): T | undefined {
  if (!windowId) {
    throw new Error('windowId has not been set')
  }
  const updates = entry.metaUpdates
    .filter(update => update.meta.sourceWindowId === windowId)
    .filter(update => update.payload.metaName === metaName)
    .map(update => update.payload.metaValue)

  return updates.length > 0 ? (updates.pop() as T) : undefined
}

export function compareEntityIdDesc(entry1: RepositoryEntry<any>, entry2: RepositoryEntry<any>): number {
  const entityId1 = parseInt(entry1.entityId, 10)
  const entityId2 = parseInt(entry2.entityId, 10)

  if (isNaN(entityId1) || isNaN(entityId2)) {
    return compareInsensitive(entry2.entityId, entry1.entityId)
  } else {
    return entityId2 - entityId1
  }
}

export const compareDateDesc = (d1: Date, d2: Date): number => {
  if (!d1 || !d2) {
    throw new Error('createdAt is not defined')
  }

  return d1 < d2 ? 1 : d1 > d2 ? -1 : 0
}

export function compareCreatedAtDesc(entry1: RepositoryEntry<any>, entry2: RepositoryEntry<any>): number {
  return compareDateDesc(entry1.lastUpdate.entity.createdAt, entry2.lastUpdate.entity.createdAt)
}

export function compareRepositoryEntryUpdateCreatedAtDesc(entry1: RepositoryEntry<any>, entry2: RepositoryEntry<any>): number {
  return compareDateDesc(entry1.lastUpdate.createdAt, entry2.lastUpdate.createdAt)
}

export function compareLastUpdatedAtDesc(entry1: RepositoryEntry<any>, entry2: RepositoryEntry<any>): number {
  return compareDateDesc(entry1.lastUpdate.entity.lastUpdatedAt, entry2.lastUpdate.entity.lastUpdatedAt)
}

export function sortCreatedAtAndReplacedWithDealId(entries: RepositoryEntry<DealUpdate | DealInterestPeriodUpdate | DealHistoryUpdate>[]) {
  return orderBy(entries, ['lastUpdate.entity.createdAt', 'lastUpdate.entity.replacedWithDealId'], ['desc', 'desc'])
}

export function sortCreatedAtDescAndUploadRowIndexAsc(entries: RepositoryEntry<OrderUpdate>[]): RepositoryEntry<OrderUpdate>[] {
  return orderBy(entries, ['lastUpdate.entity.createdAt', 'lastUpdate.entity.uploadRowIndex'], ['desc', 'asc'])
}

export function sortRfqEndTimeDescAndUploadRowIndexAsc(
  entries: RepositoryEntry<NegotiationUpdate>[]
): RepositoryEntry<NegotiationUpdate>[] {
  return orderBy(entries, ['lastUpdate.entity.rfqEndTime', 'lastUpdate.entity.uploadRowIndex'], ['asc', 'asc'])
}
